<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="mt-3 border-bottom"
    >
      <BCol
        cols="12"
        md="6"
        offset-md="6"
      >
        <ValidationObserver
          ref="formSynchronizer"
        >
          <div class="d-flex flex-column flex-md-row gap-2 justify-content-end">

            <ValidationProvider
              v-slot="{ errors }"
              vid="start_date"
              name="start date"
              rules="required"
            >
              <BFormGroup
                label="Start Date"
                label-for="synchronizeStartDate"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="synchronizeStartDate"
                  v-model="synchronize.start_date"
                  :state="(errors.length > 0 ? false : null)"
                  :disabled="state.synchronizing"
                  :max="synchronize.end_date"
                  debounce="1000"
                  type="date"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>

            <ValidationProvider
              v-slot="{ errors }"
              vid="end_date"
              name="end date"
              rules="required"
            >
              <BFormGroup
                label="End Date"
                label-for="synchronizeEndDate"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="synchronizeEndDate"
                  v-model="synchronize.end_date"
                  :state="(errors.length > 0 ? false : null)"
                  :disabled="state.synchronizing"
                  :min="synchronize.start_date"
                  debounce="1000"
                  type="date"
                />
                <div
                  v-if="(errors.length > 0)"
                  class="invalid-feedback"
                  v-text="errors[0]"
                />
              </BFormGroup>
            </ValidationProvider>

            <div class="d-flex flex-column">
              <BFormGroup
                class="mb-2"
              >
                <label
                  for="actionButtons"
                  class="d-none d-md-block"
                >
                  &nbsp;
                </label>
                <div class="d-flex gap-2">
                  <BOverlay
                    :show="state.synchronizing"
                    rounded="sm"
                    opacity="0.6"
                    spinner-small
                    spinner-variant="primary"
                  >
                    <BButton
                      :variant="`${state.synchronizing ? 'outline-dark' : 'primary'}`"
                      :disabled="(state.importing || state.synchronizing)"
                      block
                      type="button"
                      @click.prevent="synchronizeCategories"
                    >
                      Sync
                    </BButton>
                  </BOverlay>
                  <BOverlay
                    :show="state.importing"
                    rounded="sm"
                    opacity="0.6"
                    spinner-small
                    spinner-variant="secondary"
                  >
                    <BButton
                      :variant="`${state.importing ? 'outline-dark' : 'secondary'}`"
                      :disabled="(state.importing || state.synchronizing)"
                      block
                      type="button"
                      @click.prevent="importCategories"
                    >
                      Import
                    </BButton>
                  </BOverlay>
                </div>
              </BFormGroup>
            </div>
          </div>
        </ValidationObserver>
      </BCol>
    </BRow>

    <BRow
      class="mt-3"
    >
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterProductSearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterProductSearch"
            v-model="tableCategories.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Group"
          label-for="filterGroup"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableCategories.selected.productGroup"
            placeholder="search group here"
            class="vs-white"
            label="group_name"
            clearable
            append-to-body
            input-id="filterGroup"
            :options="(tableCategories.options.productGroups)"
            :loading="(tableCategories.fetching.productGroups)"
            :disabled="(tableCategories.busy || tableCategories.fetching.productGroups)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterProductGroups"
          >
            <template #option="{ group_name }">
              <div class="py-1">
                <div>{{ group_name }}</div>
              </div>
            </template>
            <template #no-options="">
              no available group
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableCategories"
      :provider="tableProvider"
      :fields="tableCategories.fields"
      :filter="tableCategories.filter"
      :filter-on="tableCategories.filterOn"
      :sort-by="tableCategories.sortBy"
      :sort-desc="tableCategories.sortDesc"
      :sort-direction="tableCategories.sortDirection"
      :total-rows="tableCategories.totalRows"
      :current-page="tableCategories.currentPage"
      :per-page="tableCategories.perPage"
      :stacked="tableCategories.stacked"
      :busy="tableCategories.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableCategories.busy"
            @click.prevent="editCategory(item)"
          >
            Edit
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalCategory"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      title="Edit Category"
    >
      <ValidationObserver
        ref="formCategory"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <BFormGroup
                label="Category Code"
                label-for="category_code"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="category_code"
                  v-model="formCategory.category_code"
                  type="text"
                  maxlength="30"
                  autocomplete="off"
                  placeholder="enter category code here"
                  disabled
                />
              </BFormGroup>
            </BCol>

            <BCol
              cols="12"
            >
              <BFormGroup
                label="Category Name"
                label-for="category_name"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="category_name"
                  v-model="formCategory.category_name"
                  type="text"
                  maxlength="100"
                  autocomplete="off"
                  placeholder="enter category name here"
                  disabled
                />
              </BFormGroup>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="group"
                name="group"
              >
                <BFormGroup
                  label="Group"
                  label-for="group"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.productGroup"
                    placeholder="search group here"
                    label="group_name"
                    clearable
                    append-to-body
                    input-id="group"
                    :options="(options.productGroups)"
                    :loading="(state.busy || fetching.productGroups)"
                    :disabled="(fetching.productGroups)"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchProductGroups"
                  >
                    <template #option="{ group_name }">
                      <div class="py-1">
                        <div>{{ group_name }}</div>
                      </div>
                    </template>
                    <template #no-options="">
                      no available group
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="is_active"
                  v-model="formCategory.active"
                  switch
                  value="1"
                  name="is_active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateCategory"
        >
          Update Category
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import QUERY from 'lodash'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MProductGroups from '@/mixins/maintenances/product-groups'

import { SAdminProductCategory } from '@/services/admin'

export default {
  name: 'AdminCategories',

  mixins: [
    MxMisc,
    MxFormatter,
    MProductGroups
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Categories'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        synchronizing: false,
        importing: false
      },
      synchronize: {
        start_date: '',
        end_date: ''
      },
      formCategory: {
        id: '',
        group_id: '',
        category_code: '',
        category_name: '',
        reference: '',
        valid: '',
        active: 1
      },
      tableCategories: {
        busy: false,
        fetching: {
          productGroups: false
        },
        options: {
          productGroups: []
        },
        selected: {
          productGroup: ''
        },
        filter: {
          search: '',
          group: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 8, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'category_code', class: 'min-w-175px' },
          { mobile: 3, key: 'category_name', class: 'min-w-175px' },
          { mobile: 4, key: 'group.group_name', label: 'Group', class: 'min-w-175px' },
          { mobile: 5, key: 'active', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 6, key: 'valid', class: 'text-center' },
          { mobile: 7, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user'
  }),

  watch: {
    'selected.productGroup' (group) {
      this.formCategory.group_id = group?.id || ''
    },

    'tableCategories.selected.productGroup' (group) {
      this.tableCategories.filter.group = group?.id || ''
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getProductGroups(this.tableCategories)
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableCategories.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        group_id: filter.group
      }

      return await SAdminProductCategory.get(payload).then(
        ({ data }) => {
          this.tableCategories.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableCategories.totalRows = 0
        return []
      }).finally(() => {
        this.tableCategories.busy = false
      })
    },

    async searchFilterProductGroups (search, loading) {
      this.searchProductGroupsProvider(this.tableCategories, search, loading)
    },

    async synchronizeCategories () {
      await this.$refs.formSynchronizer.validate().then(
        async validSynchronizer => {
          if (!validSynchronizer) {
            return this.swalInvalid()
          }

          this.state.synchronizing = true
          return SAdminProductCategory.sync(this.synchronize).then(
            ({ data: { message } }) => {
              this.swalSuccess(message).then(() => {
                this.synchronize.start_date = null
                this.synchronize.end_date = null
                this.$refs.formSynchronizer.reset()
                this.$refs.tableCategories.refetch()
              })
            }
          ).finally(() => {
            this.state.synchronizing = false
          })
        }
      )
    },

    async importCategories () {
      this.state.importing = true
      return SAdminProductCategory.import().then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.tableCategories.refetch()
          })
        }
      ).finally(() => {
        this.state.importing = false
      })
    },

    editCategory (category) {
      this.state.editing = true

      this.formCategory = Object.assign(this.formCategory, {
        id: category.id,
        group_id: category.group_id,
        category_code: category.category_code,
        category_name: category.category_name,
        reference: category.reference,
        valid: category.valid,
        active: category.active
      })

      this.getProductGroups(this).finally(() => {
        this.selected.productGroup = QUERY.find(this.options.productGroups, {
          id: Number(category.group_id)
        })
      })

      this.$refs.modalCategory.show()
    },

    async validateCategory () {
      await this.$refs.formCategory.validate().then(
        async validCategory => {
          if (!validCategory) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: '<h6>Update this Category?</h6>',
            confirmButtonText: 'Update',
            preConfirm: () => {
              this.state.busy = true
              return this.putCategory().catch(
                ({ message }) => {
                  this.$refs.formCategory.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async putCategory () {
      return await SAdminProductCategory.put(this.formCategory).then(
        ({ data: { message, product_category: category } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalCategory.hide()
            this.$refs.tableCategories.updateRow({
              id: category.id,
              group: category.group,
              group_id: category.group_id,
              category_code: category.category_code,
              category_name: category.category_name,
              active: category.active,
              reference: category.reference,
              valid: category.valid,
              updated_at: category.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
